import { DropDown } from '@finbeat/react-toolkit';
import { AxiosInstance } from 'axios';
import { Dictionary } from 'lodash';
import {
    ClientCreditLimit,
    ContractFile,
    MerchantFilterType,
    MerchantItem,
    MerchantOrderAdd,
    MerchantStatisticType,
    MerchantTableItem,
    MerchantTradeOrgContact,
    PreOrderPaymentInfo,
} from 'models/api/merchant';
import ResTable from 'models/api/table';

const controller = '/merchant';

export const getMerchantOrders = async (
    axios: AxiosInstance,
    requestParams: MerchantFilterType
) => {
    const params = { ...requestParams };
    const { data } = await axios.get<ResTable<MerchantTableItem>>(controller, { params });
    return data;
};

export const getMerchantOrdersExcel = async (axios: AxiosInstance, params: MerchantFilterType) => {
    const { data } = await axios.get<Blob>(controller + '/excel', {
        responseType: 'arraybuffer',
        params: { ...params },
    });
    return data;
};

export const getMerchantOrder = async (axios: AxiosInstance, id: string | number) => {
    const { data } = await axios.get<MerchantItem>(`${controller}/${id}`);
    return data;
};

export const addMerchantOrder = async (axios: AxiosInstance, item: MerchantOrderAdd) =>
    await axios.post(controller, item);

export const getTariffGroups = async (axios: AxiosInstance) => {
    const { data } = await axios.get<DropDown[]>(controller + '/tariffGroups');
    return data;
};

export const getMerchantOrderStatuses = async (axios: AxiosInstance) => {
    const { data } = await axios.get<DropDown[]>(controller + '/orderStatuses');
    return data;
};

export const getMerchantContacts = async (axios: AxiosInstance) => {
    const { data } = await axios.get<MerchantTradeOrgContact[]>(controller + '/tradeOrgContacts');
    return data;
};

export const getMerchantStatistic = async (axios: AxiosInstance) => {
    const { data } = await axios.get<MerchantStatisticType>(controller + '/merchantStatistic');
    return data;
};

export const getClientSelfie = async (axios: AxiosInstance, id: number | string) => {
    const response = await axios.get<Blob>(controller + `/clientSelfie/${id}`, {
        responseType: 'arraybuffer',
    });
    return response.data;
};

export const getContractFiles = async (axios: AxiosInstance, id: number | null | undefined) => {
    const { data } = await axios.get<Dictionary<number>>(`${controller}/${id}/contractFiles`);
    return data;
};

export const downloadContractFile = async (axios: AxiosInstance, docId: number | null) => {
    const response = await axios.get<Blob>(`${controller}/contractFiles/${docId}`, {
        responseType: 'arraybuffer',
    });

    const contentDisposition = response.headers['content-disposition'];
    const match = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/);
    const filename = match ? decodeURIComponent(match[1]) : 'default-filename';

    const contractFile: ContractFile = {
        filename: filename,
        src: response.data,
    };

    return contractFile;
};

export const deleteContractFile = async (axios: AxiosInstance, id: number | null) =>
    await axios.delete(controller + `/contractFiles/${id}`);

export const updateOrderStatus = async (axios: AxiosInstance, id: number | null) => {
    const { data } = await axios.get<MerchantItem>(`${controller}/updateOrderStatus/${id}`);
    return data;
};

export const getPayment = async (
    axios: AxiosInstance,
    tariffGroupId: number | null,
    sum: number,
    preorderId?: number
) => {
    const params = {
        tariffGroupId: tariffGroupId,
        sum: sum,
        preorderId: preorderId,
    };
    const { data } = await axios.get<PreOrderPaymentInfo>(`${controller}/getPayment`, {
        params: params,
    });
    return data;
};

export const getSettings = async (axios: AxiosInstance) => {
    const { data } = await axios.get<Dictionary<string>>(controller + '/settings');
    return data;
};

export const getClientLimit = async (axios: AxiosInstance, phoneNumber: string) => {
    const { data } = await axios.get<ClientCreditLimit>(controller + '/clientLimit/' + phoneNumber);
    return data;
};

export const requestClientLimit = async (axios: AxiosInstance, phoneNumber: string) => {
    const { data } = await axios.post<number>(controller + '/clientLimit/' + phoneNumber);
    return data;
};

export const getUserHasActiveOutlets = async (axios: AxiosInstance) => {
    const { data } = await axios.get<boolean>(controller + '/userHasActiveOutlets');
    return data;
};

export const getChannels = async (axios: AxiosInstance, filterByUser: boolean) => {
    const params = {
        filterByUser,
    };
    const { data } = await axios.get<DropDown[]>(`${controller}/getChannels`, {
        params: params,
    });
    return data;
};

export const getOutlets = async (axios: AxiosInstance, filterByUser: boolean) => {
    const params = {
        filterByUser,
    };

    const { data } = await axios.get<DropDown[]>(`${controller}/getOutletsDropDown`, {
        params: params,
    });
    return data;
};
